import type { ICachedAsset } from '@lucimark/shared/models/asset'
import type { ITenantPlayerBranding } from '@lucimark/shared/models/player-registration-payload'
import type { APIClientService } from '@/services/APIClientService'
import { getDb } from '@/services/DBService'

/** Last-known branding URNs for offline boot splash and idle chrome (localStorage). */
export const PLAYER_BRANDING_MANIFEST_KEY = 'LUCIMARK:PLAYER_BRANDING_MANIFEST'

export interface IPlayerBrandingManifest {
  logoUrn: string | null
  backgroundUrn: string | null
}

export function readPlayerBrandingManifest(): IPlayerBrandingManifest | null {
  try {
    const raw = localStorage.getItem(PLAYER_BRANDING_MANIFEST_KEY)
    if (raw == null || raw === '') {
      return null
    }
    const parsed = JSON.parse(raw) as IPlayerBrandingManifest
    if (typeof parsed !== 'object' || parsed === null) {
      return null
    }
    return {
      logoUrn: typeof parsed.logoUrn === 'string' ? parsed.logoUrn : null,
      backgroundUrn: typeof parsed.backgroundUrn === 'string' ? parsed.backgroundUrn : null
    }
  }
  catch {
    return null
  }
}

export function writePlayerBrandingManifest(manifest: IPlayerBrandingManifest): void {
  try {
    localStorage.setItem(PLAYER_BRANDING_MANIFEST_KEY, JSON.stringify(manifest))
  }
  catch {
    // quota / private mode
  }
}

export function manifestFromBranding(branding: ITenantPlayerBranding | undefined): IPlayerBrandingManifest {
  const logoUrn = branding?.logoUrn?.trim() || null
  const backgroundUrn = branding?.backgroundUrn?.trim() || null
  return { logoUrn, backgroundUrn }
}

/** True when a custom org logo URN is known (avoid painting Lucimark while the blob resolves). */
export function hasCustomLogoUrn(
  brandingOrManifest?: ITenantPlayerBranding | IPlayerBrandingManifest | null
): boolean {
  const logoUrn = brandingOrManifest?.logoUrn
  return typeof logoUrn === 'string' && logoUrn.trim() !== ''
}

export function readManifestHasCustomLogo(): boolean {
  return hasCustomLogoUrn(readPlayerBrandingManifest())
}

async function getCachedAssetRecord(urn: string): Promise<ICachedAsset | null> {
  const db = await getDb()
  return (await db.get('asset', urn)) ?? null
}

async function saveCachedAssetRecord(asset: ICachedAsset): Promise<void> {
  const db = await getDb()
  await db.put('asset', { ...asset, lastAccess: new Date() })
}

/**
 * Load a branding image: IndexedDB first, then network revalidation (If-None-Match).
 * Returns a blob object URL or null when unavailable offline and uncached.
 */
export async function resolveBrandingBlobUrl(
  api: APIClientService | null,
  urn: string | null | undefined
): Promise<string | null> {
  if (urn == null || urn === '') {
    return null
  }
  const cached = await getCachedAssetRecord(urn)
  if (api == null) {
    return cached?.blob instanceof Blob ? URL.createObjectURL(cached.blob) : null
  }
  try {
    const fetched = await api.fetchAssetBinary(urn, cached?.etag)
    if (fetched === null) {
      if (cached?.blob instanceof Blob) {
        return URL.createObjectURL(cached.blob)
      }
      return null
    }
    if (!(fetched.blob instanceof Blob)) {
      return cached?.blob instanceof Blob ? URL.createObjectURL(cached.blob) : null
    }
    await saveCachedAssetRecord(fetched)
    return URL.createObjectURL(fetched.blob)
  }
  catch {
    if (cached?.blob instanceof Blob) {
      return URL.createObjectURL(cached.blob)
    }
    return null
  }
}

export interface IResolvedPlayerBrandingUrls {
  logoUrl: string
  backgroundUrl: string | null
}

/** Persist manifest and resolve blob URLs for idle chrome. */
export async function resolvePlayerBrandingUrls(params: {
  branding: ITenantPlayerBranding | undefined
  api: APIClientService | null
  defaultLogoUrl: string
}): Promise<{ logoUrl: string, backgroundUrl: string | null }> {
  const { branding, api, defaultLogoUrl } = params
  const manifest = manifestFromBranding(branding)
  writePlayerBrandingManifest(manifest)

  const logoBlobUrl = await resolveBrandingBlobUrl(api, manifest.logoUrn)
  const backgroundUrl = await resolveBrandingBlobUrl(api, manifest.backgroundUrn)

  return {
    logoUrl: logoBlobUrl ?? defaultLogoUrl,
    backgroundUrl
  }
}

/** Boot splash: manifest + IndexedDB only (no auth required). */
export async function resolveBootPlayerBrandingUrls(defaultLogoUrl: string): Promise<IResolvedPlayerBrandingUrls> {
  let manifest = readPlayerBrandingManifest()
  if (manifest == null) {
    try {
      const db = await getDb()
      const snap = await db.get('registrationSnapshot', 'latest')
      const branding = snap?.payload?.tenant?.playerBranding
      if (branding != null) {
        manifest = manifestFromBranding(branding)
        writePlayerBrandingManifest(manifest)
      }
    }
    catch {
      // ignore
    }
  }
  if (manifest == null) {
    return { logoUrl: defaultLogoUrl, backgroundUrl: null }
  }
  const logoUrl = await resolveBrandingBlobUrl(null, manifest.logoUrn)
  const backgroundUrl = await resolveBrandingBlobUrl(null, manifest.backgroundUrn)
  return {
    logoUrl: logoUrl ?? defaultLogoUrl,
    backgroundUrl
  }
}
